/* eslint-disable max-lines */
import { Button, Col, Input, Modal, Row, Spin, Tag, Typography, message as antdMessage } from 'antd';
import {
  DownloadOutlined,
  ExpandAltOutlined,
  FileTextOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import DCAIcon from '../../../../../../assets/icons/GenAi/dca-icon.svg';
import { renderMarkdown } from './renderMarkdown';
import { AppSettings } from '../../../../../../AppSettings';
import './SkillsAssistant.less';

export interface ISkillsAssistantMessage {
  role: 'user' | 'assistant';
  text: string;
  content?: string;  // API payload; defaults to text — set when display text differs from what backend receives
  skill?: string;
  skillDescription?: string;
  generatedFiles?: string[];
}

interface ISkillsAssistantProps {
  initialMessages?: ISkillsAssistantMessage[];
  embedded?: boolean;       // hides sidebar/header, fills parent — for use inside SkillFlow drawer
  initialSessionId?: string | null;  // session from streaming turn; passed so follow-ups continue same session
  lockedSkill?: string;     // when set, appended as [skill:xxx] hint on every follow-up to prevent re-routing
}

interface ISession {
  session_id: string;
  title: string;
  skill: string | null;
  deployment_uuid: string | null;
  run_count: number;
  updated_at: number; // unix seconds
}

interface ISessionListResponse {
  total: number;
  limit: number;
  offset: number;
  items: ISession[];
}

interface IPolicyRunSummary {
  run_id: string;
  session_id: string;
  prompt: string;
  prompt_preview: string;
  final_text?: string;
  skill: string | null;
  skill_description?: string;
  denied: boolean;
  created_at: number;
}

interface IPolicyRunListResponse {
  total: number;
  limit: number;
  offset: number;
  items: IPolicyRunSummary[];
}

const SUGGESTED_PROMPTS = [
  'What agents are active right now?',
  'Show me Clinical Safety AI capabilities',
  'Which skills are in production?',
  'How do I configure LLM routing?',
];

const FALLBACK_RESPONSES: Record<string, string> = {
  agent:
    'There are currently 8 active agents across the platform: Orchestrator, Migration Agent, Foundation Agent, Strategy Agent, SRE Agent, Clinical Safety Agent, Regulatory AI Agent, and AIOps Agent.',
  clinical:
    'Clinical Safety AI is a Live industry solution for Life Sciences. It uses 3 specialised agents — Signal Detection Agent, Evidence Chain Builder, and Regulatory Formatter — plus the MedDRA Coding Engine and PV Database Connector.',
  skill:
    'Currently 44 skills are deployed across 8 categories: Cloud Engineering, DevOps & Platform, App Development, Banking & Finance, Life Sciences, Technology & Ops, Regulatory, and Cross-Industry.',
  llm:
    'The platform uses Claude Opus 4 for critical/multi-step tasks, Claude Sonnet 4 for standard ops, Claude Haiku 3.5 for high-volume lightweight tasks, plus models from OpenAI, Google, Meta, Mistral, AWS, and private fine-tunes. Routing defaults to Auto mode.',
  default:
    'I can help with that. For detailed information, navigate to the relevant section in the DCA Platform — Cloud Enablement for migration lifecycle, AI Engine for model and agent configuration, Industry Solutions for sector-specific capabilities, and Analytics for compliance and performance dashboards.',
};

const fallbackResponse = (msg: string) => {
  const m = msg.toLowerCase();
  if (m.includes('agent') || m.includes('active') || m.includes('running'))
    return FALLBACK_RESPONSES.agent;
  if (m.includes('clinical') || m.includes('safety') || m.includes('pharma'))
    return FALLBACK_RESPONSES.clinical;
  if (m.includes('skill') || m.includes('deployed') || m.includes('production'))
    return FALLBACK_RESPONSES.skill;
  if (m.includes('llm') || m.includes('model') || m.includes('routing') || m.includes('claude'))
    return FALLBACK_RESPONSES.llm;
  return FALLBACK_RESPONSES.default;
};

const ENGINE_BASE: string = (() => {
  const fromAppSettings = AppSettings.dcaAiEngine || '';
  if (fromAppSettings) return fromAppSettings;
  if (
    typeof window !== 'undefined' &&
    window.location &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ) {
    return 'http://127.0.0.1:8765';
  }
  return '';
})();

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

interface IEngineResponse {
  text?: string;
  skill?: string;
  skill_description?: string;
  generated_files?: string[];
  denied?: boolean;
  session_id?: string;
  run_id?: string;
}

const callEngine = async (
  prompt: string,
  history: ISkillsAssistantMessage[],
  sessionId?: string | null,
  skillHint?: string,
): Promise<IEngineResponse> => {
  const token = localStorage.getItem('accessToken');
  // Append skill routing hint silently so the backend keyword scorer always picks the right skill.
  // The hint is NOT shown in the chat — it's only in the API payload.
  const actualPrompt = skillHint ? `${prompt}\n\n[skill:${skillHint}]` : prompt;
  const body: Record<string, unknown> = {
    prompt: actualPrompt,
    history: history.map((m) => ({ role: m.role, content: m.content ?? m.text })),
    max_iterations: 30,
  };
  if (sessionId) body.session_id = sessionId;
  const resp = await fetch(`${ENGINE_BASE}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(`engine ${resp.status}: ${detail || resp.statusText}`);
  }
  return (await resp.json()) as IEngineResponse;
};

const downloadGenerated = async (paths: string[], archiveBase: string): Promise<void> => {
  if (!paths || paths.length === 0) return;
  const resp = await fetch(`${ENGINE_BASE}/api/download`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ paths, archive_name: archiveBase }),
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => '');
    throw new Error(`download ${resp.status}: ${detail || resp.statusText}`);
  }
  const blob = await resp.blob();
  const cd = resp.headers.get('Content-Disposition') || '';
  const match = cd.match(/filename="?([^"]+)"?/i);
  const fname = (match && match[1]) || `${archiveBase}.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const stamp = (): string =>
  new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');

const relativeTime = (unixSeconds: number): string => {
  const diff = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const GREETING =
  "Hi! I'm your DCA Skills Assistant, wired to the dca-ai skills engine. Ask me anything — I'll route your request through the matching skill (terraform-developer, aws-cloud-engineer, write-readme, refactor-python, add-unit-tests, …) and return the result here.";

const SkillsAssistant: React.FC<ISkillsAssistantProps> = ({ initialMessages, embedded, initialSessionId, lockedSkill }) => {
  const [messages, setMessages] = useState<ISkillsAssistantMessage[]>(
    initialMessages ?? [{ role: 'assistant', text: GREETING }],
  );
  const sessionIdRef = useRef<string | null | undefined>(initialSessionId);
  const [input, setInput] = useState<string>('');
  const [typing, setTyping] = useState<boolean>(false);
  const [typingStatus, setTypingStatus] = useState<string>('');
  const [fullOutput, setFullOutput] = useState<ISkillsAssistantMessage | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ISession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState<boolean>(false);
  const [activeSessionId, setActiveSessionId] = useState<string>('');
  const [sessionHistoryLoading, setSessionHistoryLoading] = useState<boolean>(false);
  const [nameModalOpen, setNameModalOpen] = useState<boolean>(false);
  const [pendingName, setPendingName] = useState<string>('');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typing]);

  const fetchSessions = useCallback(async () => {
    if (embedded) return;
    setSessionsLoading(true);
    try {
      const resp = await fetch(`${ENGINE_BASE}/api/sessions?limit=50`, { headers: authHeaders() });
      if (resp.ok) {
        const data = (await resp.json()) as ISessionListResponse;
        setSessions(data.items || []);
      }
    } catch {
      // Sidebar failure is non-fatal — chat still works.
    } finally {
      setSessionsLoading(false);
    }
  }, [embedded]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const loadSessionHistory = useCallback(async (sessionId: string) => {
    setSessionHistoryLoading(true);
    setMessages([{ role: 'assistant', text: GREETING }]);
    try {
      const resp = await fetch(
        `${ENGINE_BASE}/api/policy-runs?session_id=${encodeURIComponent(sessionId)}&limit=50`,
        { headers: authHeaders() },
      );
      if (!resp.ok) throw new Error(`${resp.status}`);
      const data = (await resp.json()) as IPolicyRunListResponse;
      const runs = [...(data.items || [])].reverse();
      if (runs.length === 0) {
        setMessages([{ role: 'assistant', text: GREETING }]);
      } else {
        const restored: ISkillsAssistantMessage[] = [{ role: 'assistant', text: GREETING }];
        for (const run of runs) {
          if (run.prompt_preview) {
            restored.push({ role: 'user', text: run.prompt_preview });
          }
          try {
            const detailResp = await fetch(
              `${ENGINE_BASE}/api/policy-runs/${encodeURIComponent(run.run_id)}`,
              { headers: authHeaders() },
            );
            if (detailResp.ok) {
              const detail = await detailResp.json();
              restored.push({
                role: 'assistant',
                text: detail.final_text || '(no response recorded)',
                skill: detail.skill || undefined,
              });
            }
          } catch {
            restored.push({ role: 'assistant', text: '(response not available)' });
          }
        }
        setMessages(restored);
      }
      sessionIdRef.current = sessionId;
      setActiveSessionId(sessionId);
    } catch {
      antdMessage.warning('Could not load session history.');
    } finally {
      setSessionHistoryLoading(false);
    }
  }, []);

  const startNewChat = useCallback(async (title: string) => {
    setNameModalOpen(false);
    try {
      const resp = await fetch(`${ENGINE_BASE}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ title: title.trim() }),
      });
      if (resp.ok) {
        const newSession = (await resp.json()) as ISession;
        sessionIdRef.current = newSession.session_id;
        setActiveSessionId(newSession.session_id);
        setSessions((prev) => [{ ...newSession, title: title.trim() }, ...prev]);
      }
    } catch {
      sessionIdRef.current = undefined;
      setActiveSessionId('');
    }
    setMessages([{ role: 'assistant', text: GREETING }]);
    setInput('');
  }, []);

  const send = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || typing) return;
    // Snapshot which session this send belongs to. If the user switches to a
    // different session (loadSessionHistory) before the response arrives,
    // sessionIdRef.current will no longer match — appending the reply or
    // promoting sessionIdRef at that point would land it in the wrong,
    // currently-viewed session's transcript.
    const requestSession = sessionIdRef.current;
    const userMsg: ISkillsAssistantMessage = { role: 'user', text: trimmed };
    const history = messages;
    setMessages([...history, userMsg]);
    setInput('');
    setTyping(true);
    setTypingStatus('Contacting dca-ai engine…');

    callEngine(trimmed, history, sessionIdRef.current, lockedSkill)
      .then((data) => {
        const stillOnSameSession = sessionIdRef.current === requestSession;
        if (data.session_id && !requestSession && stillOnSameSession) {
          sessionIdRef.current = data.session_id;
          setActiveSessionId(data.session_id);
          if (!embedded) fetchSessions();
        }
        if (!stillOnSameSession) return;
        const prefix = data.denied ? '🚫 Governance denied this request.\n\n' : '';
        const body = data.text || '(no response)';
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: prefix + body,
            skill: data.skill,
            skillDescription: data.skill_description,
            generatedFiles: Array.isArray(data.generated_files) ? data.generated_files : [],
          },
        ]);
      })
      .catch((err: Error) => {
        if (sessionIdRef.current !== requestSession) return;
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text:
              `Couldn't reach the dca-ai skills engine (${err.message}).\n\n` +
              `Falling back to a cached answer:\n\n${fallbackResponse(trimmed)}`,
          },
        ]);
      })
      .finally(() => {
        setTyping(false);
        setTypingStatus('');
      });
  };

  return (
    <Row className={`skills-assistant${embedded ? ' skills-assistant--embedded' : ''}`}>

      {/* Sessions sidebar — standalone mode only. span=5 matches the same
          Ant Grid column the Copilot Queries/Document Analysis sidebar uses,
          so the width is identical by construction, not by calc()-approximation.
          .skills-assistant__sidebar sits on an inner div (not the Col itself),
          same as .recent-searches-card being a Card *inside* its Col. */}
      {!embedded && (
        <Col span={5}>
        <div className="skills-assistant__sidebar">
          <div className="skills-assistant__sidebar-header">
            <span className="skills-assistant__sidebar-title">Sessions</span>
          </div>
          <div className="skills-assistant__sidebar-body">
            {sessionsLoading && (
              <div className="skills-assistant__sidebar-spin">
                <Spin size="small" />
              </div>
            )}
            {!sessionsLoading && sessions.length === 0 && (
              <div className="skills-assistant__sidebar-empty">No sessions yet</div>
            )}
            {sessions.map((s) => (
              <button
                key={s.session_id}
                type="button"
                className={`skills-assistant__session-item${
                  activeSessionId === s.session_id ? ' skills-assistant__session-item--active' : ''
                }`}
                onClick={() => {
                  if (activeSessionId !== s.session_id) loadSessionHistory(s.session_id);
                }}
              >
                <div className="skills-assistant__session-title">{s.title || 'Untitled'}</div>
                {s.skill && (
                  <Tag color="geekblue" className="skills-assistant__session-skill">
                    {s.skill.replace('dca-', '').replace('-developer', '')}
                  </Tag>
                )}
                <div className="skills-assistant__session-meta">
                  {s.run_count} {s.run_count === 1 ? 'run' : 'runs'} · {relativeTime(s.updated_at)}
                </div>
              </button>
            ))}
          </div>
          {/* Same class/style/placement as Copilot Queries' "Start New Session"
              button, for a consistent look across all 4 tabs. */}
          <Button
            className="new-session-button"
            onClick={() => { setPendingName(''); setNameModalOpen(true); }}
          >
            <Typography className="select-button-text">
              <PlusOutlined /> {'Start New Session'}
            </Typography>
          </Button>
        </div>
        </Col>
      )}

      {/* Main chat area — span=19 (or 24 when there's no sidebar) matches the
          same Ant Grid column the chat-panel uses, for the same reason as above. */}
      <Col span={embedded ? 24 : 19} className="skills-assistant__main">
        {!embedded && (
          <div className="chat-panel__header">
            <div className="chat-panel__header-left">
              <img src={DCAIcon} alt="DCA" className="chat-panel__header-icon" />
              <div>
                <div className="chat-panel__header-title">DCA Skills Assistant</div>
                <div className="chat-panel__header-sub">
                  <span className="chat-online-dot" /> Online · routed via dca-ai skills engine
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="skills-assistant__body" ref={scrollRef}>
          {sessionHistoryLoading && (
            <div className="skills-assistant__session-loading">
              <Spin size="small" />
              <span>Loading session…</span>
            </div>
          )}
          {!sessionHistoryLoading && messages.map((m, i) => (
            <div
              key={i}
              className={`skills-assistant__row skills-assistant__row--${m.role}`}
            >
              {m.role === 'assistant' && (
                <img src={DCAIcon} alt="bot" className="skills-assistant__avatar" />
              )}
              <div className="skills-assistant__bubble-wrap">
                {m.role === 'assistant' && m.skill && (
                  <Tag color="green" className="skills-assistant__skill-tag" title={m.skillDescription}>
                    Using Skill: {m.skill}
                  </Tag>
                )}
                <div className={`skills-assistant__bubble skills-assistant__bubble--${m.role}`}>
                  {m.role === 'assistant' ? (
                    <div className="skills-assistant__text skills-assistant__text--md">
                      {renderMarkdown(m.text)}
                    </div>
                  ) : (
                    <div className="skills-assistant__text">{m.text}</div>
                  )}
                  {m.role === 'assistant' &&
                    m.generatedFiles &&
                    m.generatedFiles.length > 0 && (
                      <div className="skills-assistant__files">
                        <div className="skills-assistant__files-header">
                          <strong>
                            <FileTextOutlined /> Generated files ({m.generatedFiles.length})
                          </strong>
                          <Button
                            size="small"
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloading === `zip-${i}`}
                            onClick={() => {
                              const id = `zip-${i}`;
                              setDownloading(id);
                              downloadGenerated(m.generatedFiles || [], `dca-ai-output-${stamp()}`)
                                .catch((err: Error) => { antdMessage.error(`Download failed: ${err.message}`); })
                                .finally(() => setDownloading(null));
                            }}
                          >
                            Download all (.zip)
                          </Button>
                        </div>
                        <ul>
                          {m.generatedFiles.map((f, fi) => {
                            const id = `f-${i}-${fi}`;
                            return (
                              <li key={f}>
                                <span className="skills-assistant__file-name">{f}</span>
                                <Button
                                  size="small"
                                  type="link"
                                  icon={<DownloadOutlined />}
                                  loading={downloading === id}
                                  onClick={() => {
                                    setDownloading(id);
                                    const base = f.split('/').pop() || 'file';
                                    downloadGenerated([f], base)
                                      .catch((err: Error) => { antdMessage.error(`Download failed: ${err.message}`); })
                                      .finally(() => setDownloading(null));
                                  }}
                                >
                                  Download
                                </Button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  {m.role === 'assistant' && m.text && m.text.length > 0 && (
                    <div className="skills-assistant__bubble-actions">
                      <Button
                        size="small"
                        type="link"
                        icon={<ExpandAltOutlined />}
                        onClick={() => setFullOutput(m)}
                      >
                        View full output
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {typing && (
            <div className="skills-assistant__row skills-assistant__row--assistant">
              <img src={DCAIcon} alt="bot" className="skills-assistant__avatar" />
              <div className="skills-assistant__bubble skills-assistant__bubble--assistant skills-assistant__typing">
                <span className="skills-assistant__dot-anim" />
                <span className="skills-assistant__dot-anim" />
                <span className="skills-assistant__dot-anim" />
                {typingStatus && !embedded && (
                  <span className="skills-assistant__typing-status">{typingStatus}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {!embedded && messages.length <= 1 && !sessionHistoryLoading && (
          <div className="skills-assistant__suggestions">
            {SUGGESTED_PROMPTS.map((s) => (
              <Button
                key={s}
                size="small"
                className="skills-assistant__suggestion-btn"
                onClick={() => send(s)}
              >
                {s}
              </Button>
            ))}
          </div>
        )}

        <div className="skills-assistant__input-row">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPressEnter={() => send(input)}
            placeholder="Ask the Skills Assistant…"
            className="skills-assistant__input"
            disabled={typing || sessionHistoryLoading}
          />
          <button
            type="button"
            onClick={() => send(input)}
            disabled={!input.trim() || typing || sessionHistoryLoading}
            className="skills-assistant__send-btn"
            aria-label="Send"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </Col>

      <Modal
        visible={!!fullOutput}
        zIndex={1050}
        onCancel={() => setFullOutput(null)}
        footer={[
          fullOutput && fullOutput.generatedFiles && fullOutput.generatedFiles.length > 0 ? (
            <Button
              key="dl"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => {
                if (!fullOutput || !fullOutput.generatedFiles) return;
                downloadGenerated(fullOutput.generatedFiles, `dca-ai-output-${stamp()}`)
                  .catch((err: Error) => { antdMessage.error(`Download failed: ${err.message}`); });
              }}
            >
              Download all files (.zip)
            </Button>
          ) : null,
          <Button key="close" onClick={() => setFullOutput(null)}>
            Close
          </Button>,
        ]}
        width={900}
        title={
          fullOutput && fullOutput.skill ? (
            <span>
              <Tag color="green">Using Skill: {fullOutput.skill}</Tag>
              Full output
            </span>
          ) : (
            'Full output'
          )
        }
        destroyOnClose
      >
        {fullOutput && (
          <div
            className="skills-assistant__text--md"
            style={{ maxHeight: '70vh', overflow: 'auto', background: '#ffffff', padding: '0.5rem 0.25rem' }}
          >
            {renderMarkdown(fullOutput.text)}
            {fullOutput.generatedFiles && fullOutput.generatedFiles.length > 0 && (
              <div className="skills-assistant__files" style={{ marginTop: 16 }}>
                <strong>
                  <FileTextOutlined /> Generated files ({fullOutput.generatedFiles.length})
                </strong>
                <ul>
                  {fullOutput.generatedFiles.map((f) => (
                    <li key={f}>
                      <span className="skills-assistant__file-name">{f}</span>
                      <Button
                        size="small"
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={() => {
                          const base = f.split('/').pop() || 'file';
                          downloadGenerated([f], base).catch((err: Error) => {
                            antdMessage.error(`Download failed: ${err.message}`);
                          });
                        }}
                      >
                        Download
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* New session name modal */}
      <Modal
        title="New Planning Session"
        open={nameModalOpen}
        onOk={async () => { await startNewChat(pendingName); }}
        onCancel={() => setNameModalOpen(false)}
        okText="Start"
        okButtonProps={{ disabled: !pendingName.trim() }}
        width={440}
      >
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontSize: 13, color: '#595959', marginBottom: 10 }}>
            Give this session a name so it is easy to find in Recent Planner Sessions.
          </div>
          <Input
            autoFocus
            placeholder="e.g. Cloud Migration Q3 Plan"
            value={pendingName}
            onChange={e => setPendingName(e.target.value)}
            onPressEnter={async () => { if (pendingName.trim()) await startNewChat(pendingName); }}
            maxLength={120}
            showCount
          />
        </div>
      </Modal>
    </Row>
  );
};

export default SkillsAssistant;
